import { Button, Tab, TabGroup } from "../../../../components/design-system";
import type {
  Audience,
  BudgetState,
} from "../../../../pages/Ads2DControlsAlphaBeta/shared";
import { ADS_MANAGER_SURFACE_CLASS } from "../constants/layout";
import type { SettingsTab } from "../types";

import { AdsManagerContent } from "./AdsManagerContent";
import { AdvancedSettingsTab } from "./AdvancedSettingsTab";
import { CoreSettingsTab } from "./CoreSettingsTab";

function ChevronBackIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M10 3L5 8L10 13"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type AdvertisingSettingsPageProps = {
  settingsTab: SettingsTab;
  onSettingsTabChange: (tab: SettingsTab) => void;
  onBack: () => void;
  growthIdx: number;
  onGrowthIdxChange: (idx: number) => void;
  budget: BudgetState;
  onBudgetChange: (next: Partial<BudgetState>) => void;
  audience: Audience;
  onAudienceChange: (value: Audience) => void;
  selectedStoreTypes: string[];
  newProductsBoosting: boolean;
  budgetConstrained: boolean;
  onSelectStoreTypes: () => void;
  onNewProductsBoostingChange: (value: boolean) => void;
  isDirty: boolean;
  onSave: () => void;
  onUndo: () => void;
};

export function AdvertisingSettingsPage({
  settingsTab,
  onSettingsTabChange,
  onBack,
  growthIdx,
  onGrowthIdxChange,
  budget,
  onBudgetChange,
  audience,
  onAudienceChange,
  selectedStoreTypes,
  newProductsBoosting,
  budgetConstrained,
  onSelectStoreTypes,
  onNewProductsBoostingChange,
  isDirty,
  onSave,
  onUndo,
}: AdvertisingSettingsPageProps) {
  return (
    <>
      <div
        className={`shrink-0 border-b border-[#dfe0e1] ${ADS_MANAGER_SURFACE_CLASS}`}
      >
        <AdsManagerContent className="pt-6 pb-4">
          <button
            type="button"
            className="inline-flex items-center gap-2 text-sm text-[#333] hover:underline"
            onClick={onBack}
          >
            <ChevronBackIcon />
            <span>Promoted Listings</span>
          </button>
          <div className="h-2" />
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h1 className="text-[30px] leading-[38px] font-semibold text-[#333]">
                Advertising settings
              </h1>
              <p className="mt-2 max-w-[600px] text-sm leading-5 text-[#757575]">
                Configure your Promoted Listings settings.{" "}
                <button type="button" className="underline">
                  View settings history
                </button>
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-6">
              {isDirty ? (
                <button
                  type="button"
                  className="text-sm text-[#333] underline"
                  onClick={onUndo}
                >
                  Undo changes
                </button>
              ) : null}
              <Button variant="primary" disabled={!isDirty} onClick={onSave}>
                Save
              </Button>
            </div>
          </div>
        </AdsManagerContent>
        <AdsManagerContent className="pt-2">
          <TabGroup
            selectedTabValue={settingsTab}
            onSelect={(value) => onSettingsTabChange(value as SettingsTab)}
            hideBorder
          >
            <Tab value="core" label="General" />
            <Tab value="advanced" label="Advanced" badge="Beta" />
          </TabGroup>
        </AdsManagerContent>
      </div>
      <div
        className={`min-h-0 flex-1 overflow-y-auto ${ADS_MANAGER_SURFACE_CLASS}`}
      >
        {settingsTab === "core" ? (
          <CoreSettingsTab
            growthIdx={growthIdx}
            onGrowthIdxChange={onGrowthIdxChange}
            budget={budget}
            onBudgetChange={onBudgetChange}
            audience={audience}
            onAudienceChange={onAudienceChange}
            budgetConstrained={budgetConstrained}
          />
        ) : (
          <AdvancedSettingsTab
            selectedStoreTypes={selectedStoreTypes}
            newProductsBoosting={newProductsBoosting}
            budgetConstrained={budgetConstrained}
            onSelectStoreTypes={onSelectStoreTypes}
            onNewProductsBoostingChange={onNewProductsBoostingChange}
          />
        )}
      </div>
    </>
  );
}
